import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, Bird, Bug, Shield, MapPin, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";

// Logo component - supports light (white bg logo) and dark (black bg logo) variants
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/nf6e3ut7_IMG_1549.png";

export const Logo = ({ className = "h-16", variant = "light" }) => (
  <img 
    src={variant === "dark" ? LOGO_DARK : LOGO_LIGHT}
    alt="Pest Free Ipswich & Brisbane"
    className={`${className} ${variant === "dark" ? "mix-blend-darken" : ""}`}
  />
);

// Floating button removed

// Service Areas — Regional Hubs (used in primary nav for SEO internal linking)
const SERVICE_AREAS = [
  { name: "Ipswich Region", path: "/ipswich" },
  { name: "Lockyer Valley", path: "/lockyer-valley" },
  { name: "Brisbane South", path: "/brisbane-south" },
  { name: "Brisbane West", path: "/brisbane-west" },
  { name: "Logan Corridor", path: "/logan" },
];

// Header Component - Used across all pages
export const Header = ({ onEstimateClick, onBookingClick, showBirdsCTA = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false);
  const areasRef = useRef(null);
  const location = useLocation();

  const servicePaths = new Set([
    '/pests', '/birds', '/termites',
    '/pest-control-ripley', '/termite-inspection-ripley', '/solar-bird-proofing-ripley',
    '/bird-proofing-northside-brisbane', '/bird-proofing-southside-brisbane',
    '/bird-proofing-ipswich', '/pest-control-ipswich', '/termite-inspection-ipswich',
    '/pest-control-springfield', '/pest-control-redbank-plains',
    '/ipswich', '/cockroach-treatment-ipswich', '/termite-treatment-ipswich',
    '/bird-control-ipswich', '/end-of-lease-pest-control-ipswich', '/cheap-pest-control-ipswich',
    '/lockyer-valley', '/pest-control-lockyer-valley',
    '/brisbane-south', '/pest-control-brisbane-south',
    '/brisbane-west', '/pest-control-brisbane-west',
    '/logan', '/pest-control-logan',
    '/pest-control-brassall', '/pest-control-booval', '/pest-control-bundamba',
    '/pest-control-goodna', '/pest-control-collingwood-park', '/pest-control-bellbird-park',
    '/pest-control-north-ipswich', '/pest-control-east-ipswich', '/pest-control-leichhardt',
    '/pest-control-silkstone', '/pest-control-yamanto', '/pest-control-karalee',
    '/pest-control-raceview', '/pest-control-woodend', '/pest-control-one-mile',
    '/pest-control-sadliers-crossing',
    '/quote-builder', '/refer'
  ]);
  const isServicePage = servicePaths.has(location.pathname);
  const isAreasActive = SERVICE_AREAS.some(a => a.path === location.pathname);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!areasOpen) return;
    const handleClickOutside = (e) => {
      if (areasRef.current && !areasRef.current.contains(e.target)) {
        setAreasOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [areasOpen]);

  // Close dropdown on route change
  useEffect(() => {
    setAreasOpen(false);
    setMobileAreasOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isServicePage
          ? (scrolled ? "bg-white shadow-lg py-2" : "bg-white shadow-sm py-3")
          : (scrolled ? "bg-black shadow-lg py-2" : "bg-black py-3")
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex-shrink-0"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <Logo 
              variant={isServicePage ? "dark" : "light"} 
              className={`transition-all duration-300 ${scrolled ? "h-[4rem] md:h-[4.5rem]" : "h-[4.5rem] md:h-[5.5rem]"}`} 
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link 
              to="/pests" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isActive('/pests') ? 'text-[#E74C3C]' : isServicePage ? 'text-slate-700 hover:text-[#E74C3C]' : 'text-white/90 hover:text-[#E74C3C]'}`}
            >
              <Shield className="w-4 h-4" />
              Pests
            </Link>
            <Link 
              to="/birds" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isActive('/birds') ? 'text-[#5BC0DE]' : isServicePage ? 'text-slate-700 hover:text-[#5BC0DE]' : 'text-white/90 hover:text-[#5BC0DE]'}`}
            >
              <Bird className="w-4 h-4" />
              Birds
            </Link>
            <Link 
              to="/termites" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isActive('/termites') ? 'text-[#D2691E]' : isServicePage ? 'text-slate-700 hover:text-[#D2691E]' : 'text-white/90 hover:text-[#D2691E]'}`}
            >
              <Bug className="w-4 h-4" />
              Termites
            </Link>

            {/* Service Areas Dropdown - SEO internal linking to regional hubs */}
            <div 
              ref={areasRef} 
              className="relative"
              onMouseEnter={() => setAreasOpen(true)}
              onMouseLeave={() => setAreasOpen(false)}
            >
              <button
                type="button"
                onClick={() => setAreasOpen(o => !o)}
                aria-haspopup="true"
                aria-expanded={areasOpen}
                data-testid="service-areas-trigger"
                className={`flex items-center gap-1.5 font-medium transition-colors ${isAreasActive ? 'text-[#7ABC32]' : isServicePage ? 'text-slate-700 hover:text-[#7ABC32]' : 'text-white/90 hover:text-[#7ABC32]'}`}
              >
                <MapPin className="w-4 h-4" />
                Service Areas
                <ChevronDown className={`w-4 h-4 transition-transform ${areasOpen ? 'rotate-180' : ''}`} />
              </button>
              {areasOpen && (
                <div 
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                  data-testid="service-areas-dropdown"
                >
                  <div className="bg-white rounded-xl shadow-2xl border border-slate-100 py-2 min-w-[220px] overflow-hidden">
                    {SERVICE_AREAS.map((area) => (
                      <Link
                        key={area.path}
                        to={area.path}
                        onClick={() => setAreasOpen(false)}
                        data-testid={`service-area-link-${area.path.replace('/', '')}`}
                        className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${isActive(area.path) ? 'bg-[#7ABC32]/10 text-[#7ABC32]' : 'text-slate-700 hover:bg-slate-50 hover:text-[#7ABC32]'}`}
                      >
                        <MapPin className="w-3.5 h-3.5 text-[#7ABC32]" />
                        {area.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-5 py-2.5 rounded-full transition-all">
              <Phone className="w-4 h-4" />
              (07) 3708 1422
            </a>
          </nav>

          {/* Desktop CTAs - Only show on Birds page */}
          {showBirdsCTA && (
            <div className="hidden lg:flex items-center gap-3 ml-4">
              <Button 
                onClick={onEstimateClick}
                className={`${isServicePage ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300' : 'bg-white/10 hover:bg-white/20 text-white border border-white/30'} text-sm px-5 py-2 rounded-full`}
                data-testid="header-estimate-btn"
              >
                Get Estimate
              </Button>
              <Button 
                onClick={onBookingClick}
                className="bg-[#5BC0DE] hover:bg-[#4AA8C4] text-white text-sm px-5 py-2 rounded-full"
                data-testid="header-booking-btn"
              >
                Book $20 Inspection
              </Button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button 
            className={`lg:hidden p-2 rounded-lg transition-colors ${isServicePage ? 'hover:bg-slate-100' : 'hover:bg-white/10'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen 
              ? <X className={`w-7 h-7 ${isServicePage ? 'text-slate-800' : 'text-white'}`} /> 
              : <Menu className={`w-7 h-7 ${isServicePage ? 'text-slate-800' : 'text-white'}`} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`lg:hidden border-t mt-3 py-4 rounded-b-xl ${isServicePage ? 'bg-white border-slate-200' : 'bg-black border-slate-700'}`}>
            <nav className="flex flex-col gap-1">
              <Link 
                to="/pests" 
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-medium flex items-center gap-2 ${isActive('/pests') ? 'bg-[#E74C3C]/20 text-[#E74C3C]' : isServicePage ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:bg-slate-700'}`}
              >
                <Shield className="w-5 h-5" />
                General Pest Control
              </Link>
              <Link 
                to="/birds" 
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-medium flex items-center gap-2 ${isActive('/birds') ? 'bg-[#5BC0DE]/20 text-[#5BC0DE]' : isServicePage ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:bg-slate-700'}`}
              >
                <Bird className="w-5 h-5" />
                Bird & Pigeon Control
              </Link>
              <Link 
                to="/termites" 
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-medium flex items-center gap-2 ${isActive('/termites') ? 'bg-[#D2691E]/20 text-[#D2691E]' : isServicePage ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:bg-slate-700'}`}
              >
                <Bug className="w-5 h-5" />
                Termite Management
              </Link>

              {/* Mobile Service Areas - expandable list */}
              <button
                type="button"
                onClick={() => setMobileAreasOpen(o => !o)}
                aria-expanded={mobileAreasOpen}
                data-testid="mobile-service-areas-trigger"
                className={`px-4 py-3 rounded-lg font-medium flex items-center gap-2 w-full text-left ${isAreasActive ? 'bg-[#7ABC32]/20 text-[#7ABC32]' : isServicePage ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:bg-slate-700'}`}
              >
                <MapPin className="w-5 h-5" />
                Service Areas
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${mobileAreasOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileAreasOpen && (
                <div className="ml-4 flex flex-col gap-1" data-testid="mobile-service-areas-list">
                  {SERVICE_AREAS.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-service-area-link-${area.path.replace('/', '')}`}
                      className={`px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 ${isActive(area.path) ? 'bg-[#7ABC32]/15 text-[#7ABC32]' : isServicePage ? 'text-slate-600 hover:bg-slate-100' : 'text-white/80 hover:bg-slate-700'}`}
                    >
                      <MapPin className="w-3.5 h-3.5 text-[#7ABC32]" />
                      {area.name}
                    </Link>
                  ))}
                </div>
              )}

              <a href="tel:0737081422" className="mx-4 mt-3 py-3 bg-[#7ABC32] hover:bg-[#6AAD28] text-white rounded-full flex items-center justify-center gap-2 font-semibold">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              {showBirdsCTA && (
                <div className={`px-4 pt-4 flex flex-col gap-3 border-t mt-3 ${isServicePage ? 'border-slate-200' : 'border-slate-700'}`}>
                  <Button onClick={() => { onEstimateClick?.(); setMobileMenuOpen(false); }} className={`${isServicePage ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300' : 'bg-white/10 hover:bg-white/20 text-white border border-white/30'} w-full py-3 text-base rounded-full`}>
                    Get Estimate
                  </Button>
                  <Button onClick={() => { onBookingClick?.(); setMobileMenuOpen(false); }} className="bg-[#5BC0DE] hover:bg-[#4AA8C4] text-white w-full py-3 text-base rounded-full">
                    Book $20 Inspection
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Footer Component - Used across all pages
export const Footer = () => (
  <footer className="bg-black text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-6 gap-8 mb-8">
        <div className="md:col-span-2">
          <Logo className="h-16 md:h-20 mb-6" variant="dark" />
          <p className="text-slate-400 mb-6 max-w-md text-base leading-relaxed">
            Professional pest control services in Ipswich and Brisbane. Licensed, insured, and backed by industry-leading warranties.
          </p>
          <div className="flex flex-col gap-3">
            <a href="tel:0737081422" className="flex items-center gap-3 text-slate-300 hover:text-[#7ABC32] transition-colors text-lg font-medium">
              <Phone className="w-5 h-5" /> (07) 3708 1422
            </a>
            <a href="mailto:charlie@pestfreebrisbane.com.au" className="flex items-center gap-3 text-slate-300 hover:text-[#7ABC32] transition-colors">
              <Mail className="w-5 h-5" /> charlie@pestfreebrisbane.com.au
            </a>
          </div>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4 text-white text-lg">Services</h4>
          <ul className="space-y-3">
            <li><Link to="/pests" className="text-slate-400 hover:text-[#E74C3C] transition-colors">General Pest Control</Link></li>
            <li><Link to="/birds" className="text-slate-400 hover:text-[#5BC0DE] transition-colors">Solar Bird Proofing</Link></li>
            <li><Link to="/termites" className="text-slate-400 hover:text-[#D2691E] transition-colors">Termite Management</Link></li>
            <li><Link to="/solar-bird-proofing" className="text-slate-400 hover:text-[#7ABC32] transition-colors">Solar Bird Proofing Offer</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4 text-white text-lg">Service Areas</h4>
          <ul className="space-y-3">
            <li><Link to="/bird-proofing-ipswich" className="text-slate-400 hover:text-[#5BC0DE] transition-colors">Bird Proofing Ipswich</Link></li>
            <li><Link to="/bird-proofing-northside-brisbane" className="text-slate-400 hover:text-[#5BC0DE] transition-colors">Bird Proofing Northside</Link></li>
            <li><Link to="/bird-proofing-southside-brisbane" className="text-slate-400 hover:text-[#5BC0DE] transition-colors">Bird Proofing Southside</Link></li>
            <li><Link to="/solar-bird-proofing-ripley" className="text-slate-400 hover:text-[#5BC0DE] transition-colors">Bird Proofing Ripley</Link></li>
            <li><Link to="/pest-control-ipswich" className="text-slate-400 hover:text-[#E74C3C] transition-colors">Pest Control Ipswich</Link></li>
            <li><Link to="/pest-control-springfield" className="text-slate-400 hover:text-[#E74C3C] transition-colors">Pest Control Springfield</Link></li>
            <li><Link to="/pest-control-redbank-plains" className="text-slate-400 hover:text-[#E74C3C] transition-colors">Pest Control Redbank Plains</Link></li>
            <li><Link to="/pest-control-ripley" className="text-slate-400 hover:text-[#E74C3C] transition-colors">Pest Control Ripley</Link></li>
            <li><Link to="/termite-inspection-ipswich" className="text-slate-400 hover:text-[#D2691E] transition-colors">Termite Inspection Ipswich</Link></li>
            <li><Link to="/termite-inspection-ripley" className="text-slate-400 hover:text-[#D2691E] transition-colors">Termite Inspection Ripley</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4 text-white text-lg">Credentials</h4>
          <ul className="space-y-3 text-slate-400">
            <li>QBCC: 15366887</li>
            <li>Pest License: PMT: 012086924</li>
            <li>Fully Insured</li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-white text-lg">Refer a Friend</h4>
          <ul className="space-y-3">
            <li><Link to="/refer" className="text-slate-400 hover:text-[#7ABC32] transition-colors">Refer and Earn 10%</Link></li>
            <li><Link to="/quote-builder" className="text-slate-400 hover:text-[#5BC0DE] transition-colors">Estimator for Employees</Link></li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-neutral-800 pt-8 text-center text-slate-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Pest Free Ipswich & Brisbane. All rights reserved. | Ipswich & Brisbane Pest Control</p>
      </div>
    </div>
  </footer>
);
